import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Trophy,
  Medal,
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import {
  getEvent,
  listEventTopCutResults,
  listUsers,
  listEventStandings,
  listEventPlayerResults,
  listPlayerConstructs,
} from "../../graphql/queries";
import {
  createEventTopCutResult,
  updateEventTopCutResult,
  deleteEventTopCutResult,
} from "../../graphql/mutations";
import { formatDate } from "../../utils/dateUtils";

const client = generateClient();

interface TopCutEntry {
  id?: string;
  userId?: string;
  user?: any;
  playerConstructId?: string;
  playerConstruct?: any;
  rank: number;
  bonusPoints: number;
}

interface EventParticipant {
  id: string;
  name: string;
  type: "user" | "playerConstruct";
  userId?: string;
  playerConstructId?: string;
  rank: number;
  points: number;
}

const TopCutResults: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [topCutResults, setTopCutResults] = useState<TopCutEntry[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Fetch event details
  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  // Fetch existing top cut results
  const {
    data: existingTopCutResults,
    isLoading: topCutResultsLoading,
    error: topCutResultsError,
  } = useQuery({
    queryKey: ["eventTopCutResults", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventTopCutResults,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 100,
        },
      });
      return result.data.listEventTopCutResults.items;
    },
    enabled: !!eventId,
  });

  // Fetch event standings (traditional)
  const {
    data: eventStandings,
    isLoading: standingsLoading,
    error: standingsError,
  } = useQuery({
    queryKey: ["eventStandings", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventStandings,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 100,
        },
      });
      return result.data.listEventStandings.items;
    },
    enabled: !!eventId,
  });

  // Fetch event player results (imported)
  const {
    data: eventPlayerResults,
    isLoading: playerResultsLoading,
    error: playerResultsError,
  } = useQuery({
    queryKey: ["eventPlayerResults", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventPlayerResults,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 100,
        },
      });
      return result.data.listEventPlayerResults.items;
    },
    enabled: !!eventId,
  });

  // Fetch player constructs
  const {
    data: playerConstructs,
    isLoading: playerConstructsLoading,
    error: playerConstructsError,
  } = useQuery({
    queryKey: ["playerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });

  // Fetch all users
  const {
    data: allUsers,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["allUsers"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });
      return result.data.listUsers.items;
    },
  });

  // Combine event participants from standings and player results
  const eventParticipants = useMemo(() => {
    const participants: EventParticipant[] = [];

    // Add traditional standings participants
    if (eventStandings && allUsers) {
      eventStandings.forEach((standing: any) => {
        const user = allUsers.find((u: any) => u.id === standing.userId);
        if (user) {
          participants.push({
            id: standing.userId,
            name: user.displayName || user.username || user.email,
            type: "user",
            userId: standing.userId,
            rank: standing.rank,
            points: standing.points,
          });
        }
      });
    }

    // Add imported player results participants
    if (eventPlayerResults && playerConstructs) {
      eventPlayerResults.forEach((result: any) => {
        const playerConstruct = playerConstructs.find(
          (pc: any) => pc.id === result.playerConstructId
        );
        if (playerConstruct) {
          participants.push({
            id: `construct_${result.playerConstructId}`,
            name: playerConstruct.name,
            type: "playerConstruct",
            playerConstructId: result.playerConstructId,
            rank: result.rank,
            points: result.points,
          });
        }
      });
    }

    // Sort by rank
    return participants.sort((a, b) => a.rank - b.rank);
  }, [eventStandings, eventPlayerResults, allUsers, playerConstructs]);

  // Initialize top cut results from existing data
  useEffect(() => {
    if (existingTopCutResults && allUsers && playerConstructs) {
      const resultsWithData: TopCutEntry[] = [];

      existingTopCutResults.forEach((result: any) => {
        if (result.userId) {
          // User result
          resultsWithData.push({
            id: result.id,
            userId: result.userId,
            user: allUsers.find((u: any) => u.id === result.userId),
            rank: result.rank,
            bonusPoints: result.bonusPoints,
          });
        } else if (result.playerConstructId) {
          // Player construct result
          resultsWithData.push({
            id: result.id,
            userId: `construct_${result.playerConstructId}`,
            playerConstructId: result.playerConstructId,
            playerConstruct: playerConstructs.find(
              (pc: any) => pc.id === result.playerConstructId
            ),
            rank: result.rank,
            bonusPoints: result.bonusPoints,
          });
        }
      });
      setTopCutResults(resultsWithData);
    }
  }, [existingTopCutResults, allUsers, playerConstructs]);

  // Save top cut results mutation
  const saveTopCutResultsMutation = useMutation({
    mutationFn: async (results: TopCutEntry[]) => {
      console.log("Saving top cut results:", results);

      const promises = results.map(async (result) => {
        console.log("Processing result:", result);

        if (result.id) {
          // Update existing result
          console.log("Updating existing result:", result.id);
          return client.graphql({
            query: updateEventTopCutResult,
            variables: {
              input: {
                id: result.id,
                rank: result.rank,
                bonusPoints: result.bonusPoints,
              },
            },
          });
        } else {
          // Create new result
          const input: any = {
            eventId: eventId!,
            rank: result.rank,
            bonusPoints: result.bonusPoints,
          };

          if (result.userId && !result.userId.startsWith("construct_")) {
            // Save for user
            console.log("Creating new result for user:", result.userId);
            input.userId = result.userId;
          } else if (result.userId && result.userId.startsWith("construct_")) {
            // Save for player construct
            const playerConstructId = result.userId.replace("construct_", "");
            console.log(
              "Creating new result for player construct:",
              playerConstructId
            );
            input.playerConstructId = playerConstructId;
          } else {
            console.log(
              "Skipping result - no valid user or player construct ID"
            );
            return Promise.resolve();
          }

          return client.graphql({
            query: createEventTopCutResult,
            variables: { input },
          });
        }
      });

      const saveResults = await Promise.all(promises);
      console.log("Save results:", saveResults);
    },
    onSuccess: () => {
      console.log("Top cut results saved successfully");
      setSaveError(null);
      queryClient.invalidateQueries({
        queryKey: ["eventTopCutResults", eventId],
      });
      setIsEditing(false);
    },
    onError: (error) => {
      console.error("Error saving top cut results:", error);
      setSaveError(error.message || "Failed to save top cut results");
    },
  });

  // Delete top cut result mutation
  const deleteTopCutResultMutation = useMutation({
    mutationFn: async (resultId: string) => {
      await client.graphql({
        query: deleteEventTopCutResult,
        variables: { input: { id: resultId } },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["eventTopCutResults", eventId],
      });
    },
  });

  const handleSave = () => {
    setSaveError(null);
    console.log("Attempting to save top cut results:", topCutResults);

    // Validate that all results have a player selected
    const invalidResults = topCutResults.filter((result) => !result.userId);
    if (invalidResults.length > 0) {
      setSaveError("Please select a player for all top cut results");
      return;
    }

    // Check if there are any results to save
    const validResults = topCutResults.filter((result) => result.userId);
    if (validResults.length === 0) {
      setSaveError(
        "No valid results to save. Please select players for top cut results."
      );
      return;
    }

    saveTopCutResultsMutation.mutate(topCutResults);
  };

  const handleAddResult = () => {
    setTopCutResults([
      ...topCutResults,
      {
        userId: "",
        rank: topCutResults.length + 1,
        bonusPoints: 0,
      },
    ]);
  };

  const handleRemoveResult = (index: number) => {
    const result = topCutResults[index];
    if (result.id) {
      deleteTopCutResultMutation.mutate(result.id);
    }
    setTopCutResults(topCutResults.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      const newResults = [...topCutResults];
      [newResults[index], newResults[index - 1]] = [
        newResults[index - 1],
        newResults[index],
      ];
      // Update ranks
      newResults.forEach((result, i) => {
        result.rank = i + 1;
      });
      setTopCutResults(newResults);
    }
  };

  const handleMoveDown = (index: number) => {
    if (index < topCutResults.length - 1) {
      const newResults = [...topCutResults];
      [newResults[index], newResults[index + 1]] = [
        newResults[index + 1],
        newResults[index],
      ];
      // Update ranks
      newResults.forEach((result, i) => {
        result.rank = i + 1;
      });
      setTopCutResults(newResults);
    }
  };

  const handleInputChange = (
    index: number,
    field: keyof TopCutEntry,
    value: string | number
  ) => {
    const newResults = [...topCutResults];
    newResults[index] = {
      ...newResults[index],
      [field]: value,
    };
    setTopCutResults(newResults);
  };

  const getUserName = (result: TopCutEntry) => {
    if (result.user) {
      return (
        result.user.displayName || result.user.username || result.user.email
      );
    }
    if (result.playerConstruct) {
      return result.playerConstruct.name;
    }
    // Find participant by ID
    const participant = eventParticipants.find((p) => p.id === result.userId);
    if (participant) {
      return participant.name;
    }
    return "Unknown Player";
  };

  const isLoading =
    eventLoading ||
    topCutResultsLoading ||
    standingsLoading ||
    playerResultsLoading ||
    playerConstructsLoading ||
    usersLoading;
  const hasError =
    eventError ||
    topCutResultsError ||
    standingsError ||
    playerResultsError ||
    playerConstructsError ||
    usersError;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading top cut results...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600">Error loading top cut results data</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600">Event not found</p>
        </div>
      </div>
    );
  }

  if (!event.hasTopCut) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600">This event does not have a top cut</p>
          <Button
            onClick={() => navigate(`/admin/events/${eventId}`)}
            className="mt-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Event
          </Button>
        </div>
      </div>
    );
  }

  if (eventParticipants.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600">No participants found for this event</p>
          <p className="text-muted-foreground mt-2">
            Please import event results or add standings before entering top cut
            results.
          </p>
          <Button
            onClick={() => navigate(`/admin/events/${eventId}/standings`)}
            className="mt-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go to Event Standings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Top Cut Results</h1>
          <p className="text-muted-foreground">
            {event.name} - {formatDate(event.date)}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(`/admin/events/${eventId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Event
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Top Cut Standings
          </CardTitle>
          <CardDescription>
            Enter the final rankings and bonus points for top cut participants
          </CardDescription>
        </CardHeader>
        <CardContent>
          {eventParticipants.some((p) => p.type === "playerConstruct") && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Some participants are from imported data
                (marked with "Imported"). Top cut results for these players will
                be saved and included in league standings calculations.
              </p>
            </div>
          )}
          {topCutResults.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Top Cut Results</h3>
              <p className="text-muted-foreground mb-4">
                Add top cut results to track final placements and bonus points.
              </p>
              <Button onClick={handleAddResult}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Result
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {topCutResults.map((result, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 border rounded-lg"
                >
                  {/* Rank */}
                  <div className="flex items-center gap-2">
                    <Medal className="h-4 w-4 text-yellow-500" />
                    <span className="font-semibold w-8">#{result.rank}</span>
                  </div>

                  {/* Player Selection */}
                  <div className="flex-1">
                    <Label
                      htmlFor={`player-${index}`}
                      className="text-sm font-medium"
                    >
                      Player
                    </Label>
                    <Select
                      value={result.userId}
                      onValueChange={(value) =>
                        handleInputChange(index, "userId", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select player" />
                      </SelectTrigger>
                      <SelectContent>
                        {eventParticipants?.map(
                          (participant: EventParticipant) => (
                            <SelectItem
                              key={participant.id}
                              value={participant.id}
                            >
                              {participant.name}{" "}
                              {participant.type === "playerConstruct" &&
                                "(Imported)"}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bonus Points */}
                  <div className="w-32">
                    <Label
                      htmlFor={`bonus-${index}`}
                      className="text-sm font-medium"
                    >
                      Bonus Points
                    </Label>
                    <Input
                      id={`bonus-${index}`}
                      type="number"
                      min="0"
                      value={result.bonusPoints}
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "bonusPoints",
                          parseInt(e.target.value) || 0
                        )
                      }
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === topCutResults.length - 1}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveResult(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* Add Result Button */}
              <Button
                variant="outline"
                onClick={handleAddResult}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Result
              </Button>

              {/* Error Display */}
              {saveError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">
                    <strong>Error:</strong> {saveError}
                  </p>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSave}
                  disabled={saveTopCutResultsMutation.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saveTopCutResultsMutation.isPending
                    ? "Saving..."
                    : "Save Top Cut Results"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TopCutResults;
